import React, { useReducer, useEffect, useMemo, useRef, useCallback } from 'react';
import { functionUrls } from './services/firebaseFunctions';
import './detailscardSliders.css';
import ThinkingIndicator from './components/ThinkingIndicator';
import { loginMachine } from './auth/loginMachine';

// Wikipedia API relative path
const WIKI_API_PATH = '/w/api.php';

// In-memory cache so we do not refetch for the same location+language combo.
// Key format: `${wikiLang}|${locationDisplayName}`
const TOP_SIGHTS_CACHE = new Map();

// --------------------------------------------------------------
// Helper: derive human readable name from Nominatim locationData
// --------------------------------------------------------------
const getLocationName = (loc) => {
  if (!loc) return '';
  const addr = loc.address;
  if (addr) {
    const fromAddr = addr.city || addr.town || addr.village || addr.hamlet || addr.county || addr.state || addr.country;
    if (fromAddr) return fromAddr;
  }
  return loc.display_name || addr?.display_name || loc.name || '';
};

// Calculates Haversine distance between two points on Earth
function calculateHaversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the Earth in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  console.log(`[TopSights][Haversine] (${lat1},${lon1})↔(${lat2},${lon2}) = ${distance.toFixed(2)} km`);
  return distance;
}

// --------------------------------------------------------------
// Wikipedia helpers (search + coordinates)
// --------------------------------------------------------------
async function searchWikipedia(searchTerm, langCode = 'en', signal) {
  console.log(`[TopSights][searchWikipedia] "${searchTerm}" lang=${langCode}`);
  const params = new URLSearchParams({
    action: 'query',
    list: 'search',
    srsearch: searchTerm,
    srlimit: '5',
    format: 'json',
    origin: '*',
  });
  const apiUrl = `https://${langCode}.wikipedia.org${WIKI_API_PATH}?${params.toString()}`;
  try {
    const resp = await fetch(apiUrl, { signal });
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    const res = data.query?.search || [];
    console.log(`[TopSights][searchWikipedia] found ${res.length} results for "${searchTerm}"`);
    return res;
  } catch (err) {
    if (err.name === 'AbortError') console.warn('[TopSights][searchWikipedia] aborted');
    else console.error('[TopSights][searchWikipedia] error', err);
    return [];
  }
}

async function getCoordinatesForPageIds(pageIds, langCode = 'en', signal) {
  if (!pageIds?.length) return {};
  console.log(`[TopSights][getCoords] fetching for ${pageIds.length} IDs in ${langCode}`);
  const params = new URLSearchParams({
    action: 'query',
    pageids: pageIds.join('|'),
    prop: 'coordinates',
    format: 'json',
    origin: '*',
  });
  const apiUrl = `https://${langCode}.wikipedia.org${WIKI_API_PATH}?${params.toString()}`;
  try {
    const resp = await fetch(apiUrl, { signal });
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    return data.query?.pages || {};
  } catch (err) {
    if (err.name !== 'AbortError') console.error('[TopSights][getCoords] error', err);
    return {};
  }
}

// --------------------------------------------------------------
// The main component
// --------------------------------------------------------------
export default function TopSightsCarousel({
  locationData,
  viewportWikiLanguage = 'en',
  userLanguageLabel = 'English',
  onSightsReady = () => {},
  onSightSelect = () => {},
  onPointerDown,
  fromSearchfield = false,
  searchFieldLocation = null,
  searchFieldRequestRequired = false,
  onSearchFieldRequestCompleted = () => {},
}) {
  const initialState = {
    status: 'idle', // 'idle' | 'loading' | 'error' | 'done'
    sights: [],
    error: null,
    requestKey: '',
  };

  function carouselReducer(state, action) {
    switch (action.type) {
      case 'REQUEST_START':
        return { ...initialState, status: 'loading', requestKey: action.requestKey };
      case 'ITEM_PROCESSED':
        if (action.requestKey !== state.requestKey) return state;
        return { ...state, sights: [...state.sights, action.sight] };
      case 'REQUEST_ERROR':
        if (action.requestKey !== state.requestKey) return state;
        return { ...state, status: 'error', error: action.error };
      case 'REQUEST_COMPLETE':
        if (action.requestKey !== state.requestKey) return state;
        return { ...state, status: 'done' };
      case 'CACHE_LOADED':
        return { ...state, status: 'done', sights: action.sights, requestKey: action.requestKey, error: null };
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(carouselReducer, initialState);

  // Location name (stable for current locationData)
  const locationName = useMemo(() => getLocationName(locationData), [locationData]);

  // Active request ref to manage cancellation
  const activeRequestRef = useRef({ controller: null, requestKey: '' });

  // ---------------------------------------------
  // CSV helper – parse a single CSV line (handles quoted fields)
  // ---------------------------------------------
  const parseCsvLine = (lineStr) => {
    const result = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < lineStr.length; i++) {
      const ch = lineStr[i];
      if (ch === '"') {
        if (inQuotes && lineStr[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(field.trim());
        field = '';
      } else {
        field += ch;
      }
    }
    result.push(field.trim());
    return [result[0] || '', result[1] || '', result[2] || ''];
  };

  // --------------------------------------------------------------
  // Internal helper – fetch thumbnail + coords for a given identifier
  // --------------------------------------------------------------
  async function fetchThumbAndCoords(identifier, langCode, isPageId, signal) {
    const params = new URLSearchParams({
      action: 'query',
      prop: 'pageimages|coordinates',
      piprop: 'thumbnail',
      pithumbsize: '200',
      format: 'json',
      origin: '*',
    });
    if (isPageId) params.set('pageids', identifier);
    else params.set('titles', identifier);

    const apiUrl = `https://${langCode}.wikipedia.org${WIKI_API_PATH}?${params.toString()}`;
    console.log(`[TopSights][fetchThumbAndCoords] ${apiUrl}`);
    try {
      const resp = await fetch(apiUrl, { signal });
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const pages = data.query?.pages || {};
      const pageId = Object.keys(pages)[0];
      const page = pages[pageId] || {};
      const thumb = page.thumbnail?.source || null;
      const c = page.coordinates?.[0];
      const coords = c && typeof c.lat === 'number' && typeof c.lon === 'number' ? { lat: c.lat, lon: c.lon } : null;
      const title = page.title || null;
      return { thumb, coords, title };
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[TopSights][fetchThumbAndCoords] error', err);
      return { thumb: null, coords: null, title: isPageId ? null : identifier };
    }
  }

  // --------------------------------------------------------------
  // Enrich a single sight object (thumbnail, coords, clean title)
  // --------------------------------------------------------------
  const enrichSight = async (sight, mapCenterLat, mapCenterLon, controller) => {
    const signal = controller.signal;
    let thumbnailUrl = null;
    let coordsObj = null;
    let resolvedTitle = sight.title;
    let resolvedWikiLang = viewportWikiLanguage;

    // Helper – try full text search and pick the closest match within 40km that has a thumbnail
    const tryFullTextSearch = async (term, langCode) => {
      const searchResults = await searchWikipedia(term, langCode, signal);
      if (!searchResults.length) return false;
      const pageIds = searchResults.map((r) => r.pageid);
      const coordsData = await getCoordinatesForPageIds(pageIds, langCode, signal);
      for (const res of searchResults) {
        if (signal.aborted) return false;
        const coord = coordsData[res.pageid]?.coordinates?.[0];
        if (!coord) continue;
        const distKm = calculateHaversineDistance(mapCenterLat, mapCenterLon, coord.lat, coord.lon);
        if (distKm > 40) continue;
        const tc = await fetchThumbAndCoords(res.title, langCode, false, signal);
        if (tc.thumb) {
          thumbnailUrl = tc.thumb;
          coordsObj = tc.coords || { lat: coord.lat, lon: coord.lon };
          resolvedTitle = tc.title || res.title;
          return true;
        }
      }
      return false;
    };

    try {
      // Parse link URL to extract possible identifiers
      let parsedUrl, langFromLink, titleFromLink, pageIdFromLink = null;
      try {
        parsedUrl = new URL(sight.link);
        langFromLink = parsedUrl.hostname.split('.')[0] || viewportWikiLanguage;
        titleFromLink = decodeURIComponent(parsedUrl.pathname.split('/wiki/')[1] || '').replace(/_/g, ' ').trim();
        const queryParams = new URLSearchParams(parsedUrl.search);
        if (queryParams.has('curid')) pageIdFromLink = queryParams.get('curid');
      } catch (e) {
        langFromLink = viewportWikiLanguage;
        titleFromLink = null;
      }

      resolvedWikiLang = langFromLink || viewportWikiLanguage;

      /* Attempt A – Use pageID or title from link */
      if (pageIdFromLink || titleFromLink) {
        const tc = await fetchThumbAndCoords(pageIdFromLink || titleFromLink, langFromLink, !!pageIdFromLink, signal);
        if (tc.thumb) {
          thumbnailUrl = tc.thumb;
          coordsObj = tc.coords;
          resolvedTitle = tc.title || resolvedTitle;
        } else if (tc.coords) {
          coordsObj = tc.coords;
          resolvedTitle = tc.title || resolvedTitle;
        }
      }

      /* Attempt B – Full text search with slug */
      if (!thumbnailUrl && titleFromLink) {
        await tryFullTextSearch(titleFromLink, langFromLink);
      }

      /* Attempt C – Full text search with CSV title */
      if (!thumbnailUrl) {
        await tryFullTextSearch(sight.title, langFromLink);
      }

      /* Fallback – if we have thumbnail but no coords, try to fetch coords only */
      if (thumbnailUrl && !coordsObj) {
        const tc = await fetchThumbAndCoords(resolvedTitle, langFromLink, false, signal);
        coordsObj = tc.coords;
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[TopSights][enrichSight] error', err);
    }

    const finalLink = `https://${resolvedWikiLang}.wikipedia.org/wiki/${encodeURIComponent(
      (resolvedTitle || sight.title).replace(/ /g, '_')
    )}`;

    const enriched = {
      ...sight,
      title: resolvedTitle,
      displayTitle: resolvedTitle,
      wikiTitle: resolvedTitle,
      articleTitle: resolvedTitle,
      thumbnail: thumbnailUrl,
      coords: coordsObj,
      sourceWikiLang: resolvedWikiLang,
      link: finalLink,
      originalLink: sight.link,
    };
    return enriched;
  };

  // --------------------------------------------------------------
  // Main worker – fetch CSV stream, enrich each sight, update state
  // --------------------------------------------------------------
  const fetchTopSightsForLocation = async () => {
    if (!locationName) return;

    // Determine if we should use search field parameters
    const useSearchFieldParams = fromSearchfield && searchFieldRequestRequired;

    // Build a unique key for this request
    const cacheKey = `${viewportWikiLanguage}|${userLanguageLabel}|${locationName}`;

    // If this exact request is already running, ignore
    if (activeRequestRef.current.requestKey === cacheKey && state.status === 'loading') {
      return;
    }

    // Abort any previous in-flight request
    if (activeRequestRef.current.controller) {
      activeRequestRef.current.controller.abort();
    }

    // Serve from cache if available (no network)
    if (TOP_SIGHTS_CACHE.has(cacheKey)) {
      const cached = TOP_SIGHTS_CACHE.get(cacheKey) || [];
      if (cached.length > 0) {
        console.log(`[TopSights] cache hit for ${cacheKey}`);
        dispatch({ type: 'CACHE_LOADED', sights: cached, requestKey: cacheKey });
        onSightsReady(
          cached.filter((s) => s.thumbnail && s.coords).map((s) => ({
            id: s.link || s.title,
            title: s.title,
            lat: s.coords?.lat,
            lon: s.coords?.lon,
            thumbnail: s.thumbnail,
            link: s.link,
            description: s.description,
            articleTitle: s.wikiTitle || s.articleTitle,
            sourceWikiLang: s.sourceWikiLang || viewportWikiLanguage,
            isTopSight: true,
          }))
        );

        // Call completion callback if this was a search field request
        if (useSearchFieldParams) {
          onSearchFieldRequestCompleted();
        }
        return;
      }

      // Stale/empty cache entries should not block a fresh request.
      TOP_SIGHTS_CACHE.delete(cacheKey);
    }

    // No cache – start a fresh fetch
    dispatch({ type: 'REQUEST_START', requestKey: cacheKey });

    const controller = new AbortController();
    activeRequestRef.current = { controller, requestKey: cacheKey };

    const lat = locationData?.lat || locationData?.lat_lon?.split(',')[0];
    const lon = locationData?.lon || locationData?.lat_lon?.split(',')[1];
    const gps = lat && lon ? `${lat},${lon}` : '';

    const params = new URLSearchParams({
      location: useSearchFieldParams && searchFieldLocation ? searchFieldLocation : locationName,
      gpslatlon: useSearchFieldParams ? '' : gps,
      userlangSpelldout: userLanguageLabel,
      wikiLang: viewportWikiLanguage,
      fromSearchfield: useSearchFieldParams.toString(),
    });

    // Use environment-aware function URL
    const url = `${functionUrls.getLocationSightsGemini31FlashLitePreview}?${params.toString()}`;
    console.log(`[TopSights] CSV URL: ${url}`);

    const processedSights = [];

    try {
      loginMachine.recordUsage('llm');
      let idToken = null;
      try {
        idToken = await loginMachine.getIdToken();
      } catch (error) {
        console.error('[TopSights] Error fetching ID token:', error);
        dispatch({ type: 'REQUEST_ERROR', error: 'could not connect to authentication service', requestKey: cacheKey });
        return;
      }

      const headers = { Accept: 'text/plain' };
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`;
      }

      const resp = await fetch(url, { headers, signal: controller.signal });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      if (!resp.body) throw new Error('ReadableStream not available');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let headerSkipped = false;

      const processLine = async (lineStr) => {
        const [link, title, description] = parseCsvLine(lineStr);
        if (!link || !title) return;
        const basicSight = { link, title, description };
        const enriched = await enrichSight(basicSight, parseFloat(lat), parseFloat(lon), controller);
        processedSights.push(enriched);
        dispatch({ type: 'ITEM_PROCESSED', sight: enriched, requestKey: cacheKey });
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) await processLine(buffer.trim());
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 1);
          if (!line) continue;
          if (!headerSkipped) {
            const lower = line.toLowerCase();
            if (lower.includes('link') && lower.includes('title')) {
              headerSkipped = true;
              continue; // skip header
            }
            headerSkipped = true;
          }
          await processLine(line);
        }
      }

      if (!controller.signal.aborted) {
        if (processedSights.length > 0) {
          TOP_SIGHTS_CACHE.set(cacheKey, processedSights);
        }
        dispatch({ type: 'REQUEST_COMPLETE', requestKey: cacheKey });

        // Call completion callback if this was a search field request
        if (useSearchFieldParams) {
          onSearchFieldRequestCompleted();
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.warn('[TopSights] request aborted', cacheKey);
      } else {
        console.error('[TopSights] error', err);
        dispatch({ type: 'REQUEST_ERROR', error: 'could not connect to sights service', requestKey: cacheKey });
      }
    }
  };

  // Inform parent whenever we have displayable sights
  useEffect(() => {
    const displayable = state.sights.filter((s) => s.thumbnail && s.coords);
    if (displayable.length) {
      onSightsReady(
        displayable.map((s) => ({
          id: s.link || s.title,
          title: s.title,
          lat: s.coords.lat,
          lon: s.coords.lon,
          thumbnail: s.thumbnail,
          link: s.link,
          description: s.description,
          articleTitle: s.wikiTitle || s.articleTitle,
          sourceWikiLang: s.sourceWikiLang || viewportWikiLanguage,
          isTopSight: true,
        }))
      );
    }
  }, [state.sights, onSightsReady, viewportWikiLanguage]);

  useEffect(() => {
    fetchTopSightsForLocation();
    console.log('[TopSights] Fetching sights for location snapshot:', locationName, 'with language:', viewportWikiLanguage);
  }, [
    locationName,
    viewportWikiLanguage,
    userLanguageLabel,
    fromSearchfield,
    searchFieldLocation,
    searchFieldRequestRequired,
  ]);

  /* ------------------------------------------------------------------ */
  /* Manual horizontal scrolling via pointer drag                        */
  /* ------------------------------------------------------------------ */

  // Ref to the element that actually scrolls horizontally
  const carouselScrollRef = useRef(null);

  // Forward the incoming onPointerDown prop to DetailsCard while also
  // preparing for horizontal-drag detection.
  const handlePointerDownForward = useCallback((e) => {
    if (onPointerDown) onPointerDown(e);
  }, [onPointerDown]);

  useEffect(() => {
    const el = carouselScrollRef.current;
    if (!el) return;

    // Allow browser to manage vertical pans (for DetailsCard drag) while we
    // take over horizontal pans. Equivalent to the old CSS rule.
    el.style.touchAction = 'pan-x';

    let isDragging = false;
    let mode = 'pending'; // 'pending' | 'horizontal' | 'vertical'
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;

    const ADMIT_THRESHOLD = 8; // px

    const onPointerDownLocal = (e) => {
      // Only react to primary button / primary touch contact
      if (e.button && e.button !== 0) return;

      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = el.scrollLeft;
      isDragging = true;
      mode = 'pending';

      // Do NOT capture pointer yet – wait until we are sure the user intends
      // a horizontal gesture. This keeps vertical drags feeling silky because
      // the pointer can still bubble up to the DetailsCard handler and we
      // don't hijack the event too early.

      handlePointerDownForward(e);
    };

    const onPointerMoveLocal = (e) => {
      if (!isDragging) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (mode === 'pending') {
        if (Math.abs(dx) > ADMIT_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.1) {
          mode = 'horizontal';
        } else if (Math.abs(dy) > ADMIT_THRESHOLD && Math.abs(dy) > Math.abs(dx)) {
          // User intends vertical movement → hand off to DetailsCard drag logic
          mode = 'vertical';
          isDragging = false;
          return;
        } else {
          return; // wait for clearer intent
        }
      }

      if (mode === 'horizontal') {
        const newScroll = startScrollLeft - dx; // dragging left scrolls right
        el.scrollLeft = newScroll;
        e.preventDefault(); // Prevent browser overscroll / text selection
        e.stopPropagation(); // Stop event so DetailsCard doesn't treat as a card drag

        // Now that we know it's a horizontal drag, capture the pointer so
        // subsequent moves stay with the carousel even if the finger moves
        // outside its bounding box.
        try { el.setPointerCapture?.(e.pointerId); } catch {}
      }
    };

    const endDrag = (e) => {
      isDragging = false;
      mode = 'pending';

      // Release capture just in case
      if (e?.pointerId !== undefined) {
        try { el.releasePointerCapture?.(e.pointerId); } catch {}
      }
    };

    el.addEventListener('pointerdown', onPointerDownLocal);
    el.addEventListener('pointermove', onPointerMoveLocal);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    return () => {
      el.removeEventListener('pointerdown', onPointerDownLocal);
      el.removeEventListener('pointermove', onPointerMoveLocal);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
    };
  }, [handlePointerDownForward]);

  // ------------------------------
  // Render section
  // ------------------------------
  if (state.status === 'loading' && !state.sights.length) {
    return (
      <ThinkingIndicator style={{ margin: '8px 0' }} />
    );
  }

  if (state.status === 'error') {
    return (
      <div style={{ padding: '10px', textAlign: 'center', color: '#666', margin: '8px 0' }}>
        {state.error}
      </div>
    );
  }

  if (state.status === 'done' && !state.sights.length) {
    return null;
  }

  const sightsWithThumb = state.sights.filter((s) => s.thumbnail);
  if (!sightsWithThumb.length) {
    return (
      <div
        style={{
          padding: '10px',
          textAlign: 'center',
          fontStyle: 'italic',
          color: '#aaa',
          margin: '8px 0',
        }}
      >
      </div>
    );
  }

      return (
      <div className="sights-carousel-container slider-container" onPointerDown={handlePointerDownForward}>
        <div ref={carouselScrollRef} className="sights-carousel slider-horizontal">
          {sightsWithThumb.map((s, i) => (
            <div
              key={i}
              className="sight-card-carousel slider-card"
              style={{ cursor: 'pointer' }}
              onClick={() => onSightSelect(s)}
            >
              <div className="sight-card-thumbnail-container slider-card-thumbnail-container">
                <img src={s.thumbnail} alt={s.title} className="sight-card-image slider-card-image" />
                <div className="sight-card-title-overlay slider-card-title-overlay">
                  <strong>{s.title}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
}
